import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { adminLogin as apiAdminLogin, type AdminUser } from "@/lib/admin-api-client";

/**
 * Session admin côté SPA — pendant admin de `customer-auth-context.tsx` (même
 * doc de tête à lire pour le détail), remplace apps/web/lib/admin-auth-session.ts
 * + lib/admin-auth-actions.ts. Même principe : état UI non sensible
 * uniquement (`admin` — id/email/prénom/nom/rôles — jamais le jeton), le
 * cookie httpOnly `vvs_admin_access_token` (une fois posé directement par
 * apps/api) reste la seule source de vérité, invisible depuis ce code.
 */

const STORAGE_KEY = "vvs_admin_ui";

export type AdminAuthUser = AdminUser;

export interface AdminAuthContextValue {
  admin: AdminAuthUser | null;
  isLoggedIn: boolean;
  login: (email: string, password: string) => Promise<void>;
  /**
   * Efface uniquement l'état UI local. TODO(auth API) : comme côté client
   * (cf. customer-auth-context.tsx), aucun endpoint `POST /admin/auth/logout`
   * n'existe encore pour invalider le cookie httpOnly côté serveur — à
   * appeler ici une fois disponible.
   */
  logout: () => void;
}

const AdminAuthContext = createContext<AdminAuthContextValue | null>(null);

function readStoredAdmin(): AdminAuthUser | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AdminAuthUser;
  } catch {
    return null;
  }
}

function writeStoredAdmin(admin: AdminAuthUser | null): void {
  try {
    if (admin) {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(admin));
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Stockage indisponible : sans gravité, état gardé en mémoire pour cette visite.
  }
}

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [admin, setAdmin] = useState<AdminAuthUser | null>(() => readStoredAdmin());

  const login = useCallback(async (email: string, password: string) => {
    const result = await apiAdminLogin(email, password);
    // Ne retient QUE l'admin (non sensible) — accessToken délibérément ignoré,
    // cf. commentaire de tête de fichier.
    setAdmin(result.admin);
    writeStoredAdmin(result.admin);
  }, []);

  const logout = useCallback(() => {
    setAdmin(null);
    writeStoredAdmin(null);
  }, []);

  const value = useMemo<AdminAuthContextValue>(
    () => ({ admin, isLoggedIn: admin !== null, login, logout }),
    [admin, login, logout],
  );

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

export function useAdminAuth(): AdminAuthContextValue {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) {
    throw new Error("useAdminAuth() doit être utilisé à l'intérieur de <AdminAuthProvider>.");
  }
  return ctx;
}
